import React, { forwardRef, useCallback } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { styled } from '@compiled/react';

import type { SVGProps } from '@atlaskit/icon';
import { token } from '@atlaskit/tokens';
import { B50, B400, N20A, N30, N500 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { FeedType } from '../useFeedTypeSelector';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-button -- Ignored via go/DSP-18766
const FeedTypePillButton = styled.button<{
	selected: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.selected
			? token('color.background.selected', B50)
			: token('color.background.neutral', N20A),
	display: 'inline-flex',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.selected ? token('color.text.brand', B400) : token('color.text', N500)),
	marginTop: '0px',
	userSelect: 'none',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.selected
				? token('color.background.selected.hovered', B50)
				: token('color.background.neutral.hovered', N30),
	},
	padding: token('space.100', '8px'),
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props) =>
		props.selected
			? // TODO: Change B400 here to B700 when it becomes available
				`1px solid ${token('color.text.selected', B400)}`
			: `1px solid ${token('color.background.neutral', N20A)}`,
	borderRadius: '100px',
	height: '32px',
	marginRight: token('space.100', '8px'),
	'&:focus': {
		outline: `2px solid ${token('color.border.focused', B400)}`,
		borderColor: 'transparent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	height: '100%',
	alignItems: 'center',
	marginRight: token('space.050', '4px'),
});

type FeedTypePillProps = {
	feedType: FeedType | string;
	selectedFeedType: FeedType | string;
	setFeedType: (newFeedType: FeedType | string) => void;
	Icon?: (props: SVGProps) => JSX.Element;
	children?: ReactNode;
};

export const FeedTypePill = forwardRef<HTMLButtonElement, FeedTypePillProps>(
	({ feedType, selectedFeedType, setFeedType, Icon, children }, ref) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleClick = useCallback(() => {
			if (selectedFeedType !== feedType) {
				setFeedType(feedType);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'feedTypeSelector',
						actionSubjectId: feedType.toLowerCase(),
						source: 'homeScreen',
						attributes: {
							homeVersion: 'visualRefresh',
						},
					},
				}).fire();
			}
		}, [feedType, selectedFeedType, setFeedType, createAnalyticsEvent]);

		const handleMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
			// prevent focus
			event.preventDefault();
		}, []);

		const selected = selectedFeedType === feedType;
		const testId = `${feedType.toLowerCase()}-feed-button`;
		return (
			<FeedTypePillButton
				selected={selected}
				aria-pressed={selected}
				onClick={handleClick}
				onMouseDown={handleMouseDown}
				data-testid={testId}
				ref={ref}
			>
				{Icon && (
					<IconWrapper>
						<Icon
							label=""
							primaryColor={
								selected ? token('color.text.selected', B400) : token('color.text', N500)
							}
						/>
					</IconWrapper>
				)}
				{children}
			</FeedTypePillButton>
		);
	},
);
